import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import {
  PRESTATION_TYPES,
  getAvailablePrestationTypes,
  getPrestationTypeMeta,
} from '../config/prestation-types';

/**
 * Choix du type de prestation (cartes) — remplace le menu déroulant technique.
 * @param {'form' | 'filter'} mode — filter ajoute l'option « Tous »
 */
export default function PrestationTypePicker({
  value,
  onChange,
  commercant,
  mode = 'form',
  disabled = false,
  className = '',
}) {
  const types = getAvailablePrestationTypes(commercant);
  const selected = getPrestationTypeMeta(value);
  const extraType =
    value && !types.some((t) => t.id === value)
      ? {
          id: value,
          label: value,
          icon: '📌',
          description: 'Type actuel de cette prestation',
          hint: '',
        }
      : null;
  const displayTypes = extraType ? [extraType, ...types] : types;

  const select = (id) => {
    if (disabled) return;
    onChange(id);
  };

  return (
    <div className={className}>
      {mode === 'form' && (
        <p className="text-xs text-gray-500 mb-2">
          Choisissez comment les clients réserveront cette prestation.
        </p>
      )}
      <div
        className={`grid gap-2 ${
          mode === 'filter' ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2'
        }`}
        role="group"
        aria-label={mode === 'filter' ? 'Filtrer par type de prestation' : 'Type de prestation'}
      >
        {mode === 'filter' && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => select('')}
            className={`rounded-xl border p-3 text-left transition ${
              value === ''
                ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-200'
                : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <span className="text-lg block mb-1">📋</span>
            <span className="text-sm font-semibold text-gray-900">Tous</span>
            <span className="text-xs text-gray-500 mt-0.5 block">Tous les types</span>
          </button>
        )}
        {displayTypes.map((type) => {
          const active = value === type.id;
          return (
            <button
              key={type.id}
              type="button"
              disabled={disabled}
              onClick={() => select(type.id)}
              className={`relative rounded-xl border p-3 text-left transition ${
                active
                  ? 'border-indigo-600 bg-indigo-50 ring-2 ring-indigo-200'
                  : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {active && (
                <CheckCircle2 className="absolute top-2 right-2 h-4 w-4 text-indigo-600" aria-hidden />
              )}
              <span className="text-xl block mb-1">{type.icon}</span>
              <span className="text-sm font-semibold text-gray-900 pr-5">{type.label}</span>
              <span className="text-xs text-gray-500 mt-1 block leading-snug">{type.description}</span>
              {mode === 'form' && (
                <span className="text-xs text-indigo-600/80 mt-1 block">{type.hint}</span>
              )}
            </button>
          );
        })}
      </div>
      {mode === 'form' && selected && (
        <p className="mt-2 text-xs text-gray-600 rounded-lg bg-gray-50 px-3 py-2 border border-gray-100">
          <strong className="text-gray-800">{selected.label}</strong>
          {' — '}
          {selected.hint}
        </p>
      )}
      {mode === 'filter' && !value && (
        <p className="mt-2 text-xs text-gray-500">Affichage de toutes les prestations.</p>
      )}
    </div>
  );
}

/** Libellés compacts pour badges / listes */
export { PRESTATION_TYPES };
